import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";

export interface NormalizedApiResponse<T = any> {
    success: boolean;
    statusCode: number;
    message?: string;
    data?: T;
}

/**
 * Normalizes RTK Query hook results into a single shape.
 * When neither `isError` nor `isSuccess` is true (e.g. loading or uninitialized), returns `message: "Unknown state"`.
 */
export const normalizeAPIResponse = <T = any>(
    result: {
        data?: any;
        error?: FetchBaseQueryError | SerializedError;
        isSuccess: boolean;
        isError: boolean;
    }
): NormalizedApiResponse<T> => {

    if (result.isError) {
        const error = result.error;

        let statusCode = 500;
        let message = "Something went wrong. Please try again.";
        let errorData: unknown = undefined;

        if (error && "status" in error) {
            const fetchErr = error as FetchBaseQueryError;
            statusCode = typeof fetchErr.status === "number" ? fetchErr.status : 500;

            if (typeof fetchErr.data === "object" && fetchErr.data !== null && "message" in fetchErr.data) {
                message = (fetchErr.data as { message?: string }).message || message;
            } else if (typeof fetchErr.data === "string") {
                message = fetchErr.data;
            }
            errorData = fetchErr.data;
        } else if (error && "message" in error) {
            message = error.message || message;
        }

        return {
            success: false,
            statusCode,
            message,
            data: errorData as T | undefined
        };
    }

    if (result.isSuccess) {
        const actualData = result.data;
        const hasBody = actualData != null;
        const httpStatus = actualData?._meta?.httpStatus ?? actualData?.statusCode ?? 200;

        return {
            success: (actualData.success === true || httpStatus === 200  ) ? true : false,
            statusCode: httpStatus,
            message: actualData?.message,
            data: hasBody ? (actualData as T) : undefined
        };
    }

    return {
        success: false,
        statusCode: 500,
        message: "Unknown state"
    };
};
